/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import * as _ from 'lodash';
import BWDataService from './bw-data.service';
import FormatService from './format.service';

/*
 * Return values of canAffordHome function.
 * Note that this uses binary/bitwise logic, so the 0,1,2,3 values are intentional.
 */
export enum CanAffordHome {
    ok = 0,
    useSavings = 1,
    tooExpensive = 2,
    monthlyPaymentTooHigh = 3,
    downPaymentTooHigh = 4,
    monthlyAndDownPaymentTooHigh = 5
}

export enum CanAffordPurchase {
    ok = 0,
    useSavings = 1,
    tooExpensive = 2
}

export interface SocialSecurityResponse {
    estimated: number;
    percentage: number;
}

class FinanceService extends BWDataService {
    public static discountFactor(amount: number, apr: number, months: number): number {
        return (Math.pow(1 + apr / 12, months) - 1) / ((apr / 12) * Math.pow(1 + apr / 12, months));
    }

    public static getMonthlyPayment(amount: number, apr: number, months: number): number {
        if (!apr) {
            return amount / months;
        }
        return amount / FinanceService.discountFactor(amount, apr / 100, months);
    }

    /** Maximum mortgage debt to income ration */
    private static readonly MAXIMUM_MORTGAGE_DEBT_TO_INCOME = 0.28;

    /**
     * Check if the user can afford the home purchase on the given date. The last two arguments are from the
     * Buy New Home Card's detailed payload.
     * @param date of the card (ie: startDate), or whenever the user plans to purchase the house
     * @param cashDueOnPurchase probably downPayment - existing home profit
     * @param monthlyPayment calculated by calling getMonthlyPayment
     * @param monthlySavings from the BuyNewHome card detailed payload
     * @param monthlyGrossIncome from the BuyNewHome card detailed payload
     */
    public static canAffordHome(
        date: LocalDate,
        cashDueOnPurchase: number,
        monthlyPayment: number,
        monthlySavings: any,
        monthlyGrossIncome: any,
        monthlyFreeCash: any
    ): CanAffordHome {
        const month: string = date.toJSON();
        const grossIncome: number = Number(_.get(monthlyGrossIncome, month, 0));
        const savings: number = Number(_.get(monthlySavings, month, 0.0));

        const canAffordMonthlyPayment: boolean = monthlyPayment <= grossIncome * this.MAXIMUM_MORTGAGE_DEBT_TO_INCOME;
        const canAffordDownPayment: boolean = cashDueOnPurchase <= savings;

        if (!canAffordMonthlyPayment && !canAffordDownPayment) return CanAffordHome.monthlyAndDownPaymentTooHigh;
        if (!canAffordMonthlyPayment) return CanAffordHome.monthlyPaymentTooHigh;
        if (!canAffordDownPayment) return CanAffordHome.downPaymentTooHigh;

        const freeCash: number = Number(_.get(monthlyFreeCash, month, 0.0));

        if (cashDueOnPurchase <= freeCash) return CanAffordHome.ok;

        return CanAffordHome.useSavings;
    }

    public static canAffordPurchase(
        date: LocalDate,
        cashDueOnPurchase: number,
        monthlySavings: any,
        monthlyFreeCash: any
    ): CanAffordPurchase {
        if (cashDueOnPurchase === 0) {
            return CanAffordPurchase.ok;
        }
        const month: string = date.toJSON();
        const freeCash: number = Number(_.get(monthlyFreeCash, month, 0.0));
        if (cashDueOnPurchase < freeCash) return CanAffordPurchase.ok;

        const savings: number = Number(_.get(monthlySavings, month, 0.0));
        if (cashDueOnPurchase <= savings) return CanAffordPurchase.useSavings;

        return CanAffordPurchase.tooExpensive;
    }

    // You don't have to have enough money before your kid starts college just, in this model, before each year
    public static canAffordMonthly(
        startDate: LocalDate,
        endDate: LocalDate,
        monthlyCost: number,
        monthlySavings: any,
        monthlyFreeCash: any
    ): CanAffordPurchase {
        let alreadySpent: number = 0;
        let canAfford: CanAffordPurchase = CanAffordPurchase.ok;

        let date: LocalDate = startDate;

        while (date.isBefore(endDate)) {
            const month: string = date.toJSON();
            const savings: number = Number(_.get(monthlySavings, month, 0.0));
            const freeCash: number = Number(_.get(monthlyFreeCash, month, 0.0));

            if (monthlyCost <= freeCash - alreadySpent) {
                canAfford = Math.max(CanAffordPurchase.ok, canAfford);
            } else {
                if (monthlyCost <= savings - alreadySpent) {
                    canAfford = Math.max(CanAffordPurchase.useSavings, canAfford);
                } else {
                    canAfford = CanAffordPurchase.tooExpensive;
                }
            }

            alreadySpent += monthlyCost;
            date = date.plusMonths(1);
        }

        return canAfford;
    }

    // You don't have to have enough money before your kid starts college just, in this model, before each year
    public static canAffordEducation(
        date: LocalDate,
        years: number,
        costPerYear: number,
        monthlySavings: any,
        monthlyFreeCash: any
    ): CanAffordPurchase {
        let alreadySpent: number = 0;
        let canAfford: CanAffordPurchase = CanAffordPurchase.ok;

        for (let year: number = 0; year < years && canAfford !== CanAffordPurchase.tooExpensive; year++) {
            const month: string = date.toJSON();
            const savings: number = Number(_.get(monthlySavings, month, 0.0));
            const freeCash: number = Number(_.get(monthlyFreeCash, month, 0.0));

            if (costPerYear <= freeCash - alreadySpent) {
                canAfford = Math.max(CanAffordPurchase.ok, canAfford);
            } else {
                if (costPerYear <= savings - alreadySpent) {
                    canAfford = Math.max(CanAffordPurchase.useSavings, canAfford);
                } else {
                    canAfford = CanAffordPurchase.tooExpensive;
                }
            }

            alreadySpent += costPerYear;
            date = date.plusYears(1);
        }

        return canAfford;
    }

    public static getFutureValue(amount: number, apr: number, months: number): number {
        return amount * Math.pow(1 + apr / 12, months);
    }

    public static getFutureValueAnnually(amount: number, apr: number, years: number): number {
        return amount * Math.pow(1 + apr, years);
    }

    public static getPresentValue(amount: number, apr: number, months: number): number {
        return amount / Math.pow(1 + apr / 12, months);
    }

    public static estimatedApr(months: number): number {
        if (months < 32) {
            return 0;
        }
        if (months < 80) {
            return 0.02;
        }
        if (months < 120) {
            return 0.06;
        }
        return 0.08;
    }

    // given the estimated annual income over 35 years, what is the monthly social security
    // maximum income you can collect for is $128,400
    public static async retrieveSocialSecurity(startDate: LocalDate, income: number): Promise<SocialSecurityResponse> {
        return await this.get(`/socialsecurity/${FormatService.formatSqlDate(startDate)}/${income}`);
    }

    public static estimatedSocialSecurity(annualIncome: number): number {
        const maxAnnualIncome = 142800;
        const firstPoint = 996;
        const secondPoint = 6002;

        const monthlyIncome = Math.min(maxAnnualIncome, Math.max(annualIncome, 0)) / 12;

        let total = Math.min(firstPoint, monthlyIncome) * 0.9;
        total += Math.max(0, Math.min(secondPoint, monthlyIncome) - firstPoint) * 0.32;
        total += Math.max(monthlyIncome - secondPoint, 0) * 0.15;

        return total;
    }

    // assumes For Single Filers 2023 numbers
    // TODO ask filing status or make dependent on having a spouse
    // https://taxfoundation.org/2023-tax-brackets/
    public static incomeTaxAmount(annualIncome: number): number {
        switch (true) {
            case annualIncome <= 11000:
                return 0.1 * annualIncome;
            case annualIncome <= 44725:
                return 987.5 + 0.12 * (annualIncome - 11000);
            case annualIncome <= 95375:
                return 4617.5 + 0.22 * (annualIncome - 44725);
            case annualIncome <= 182100:
                return 14605.5 + 0.24 * (annualIncome - 95375);
            case annualIncome <= 231250:
                return 33271.5 + 0.32 * (annualIncome - 182100);
            case annualIncome <= 578125:
                return 47367.5 + 0.35 * (annualIncome - 231250);
            default:
                return 156235 + 0.37 * (annualIncome - 578125);
        }
    }
}

export default FinanceService;
